import { create } from 'zustand';
import { v4 as uuid } from 'uuid';
import * as api from '@/apis/boards';
import { toast } from '@/utils/toastManager';
import type { Board, BoardMessage } from './types';
import type { BoardDetailDTO, BoardSummaryDTO } from '@/types/boards';

type Ctx = { orgUUID: string; userUUID: string };

function summaryToBoard(d: BoardSummaryDTO): Board {
  return {
    id: d.uuid,
    title: d.title,
    author: d.author ?? 'You',
    createdAt: Date.parse(d.createdAt) || Date.now(),
    updatedAt: Date.parse(d.updatedAt) || Date.now(),
  };
}

function detailToBoard(d: BoardDetailDTO): Board {
  return {
    id: d.uuid,
    title: d.title,
    author: d.author ?? 'You',
    createdAt: Date.parse(d.createdAt) || Date.now(),
    updatedAt: Date.parse(d.updatedAt) || Date.now(),
    // Whole-board c1 artifact + the conversation that produced it. The board
    // page renders artifactContent directly and only re-streams the model on a
    // new prompt.
    messages: d.messages ?? undefined,
    artifactContent: d.artifactContent ?? undefined,
    generationStatus: d.generationStatus ?? null,
  };
}

interface BoardsState {
  boards: Board[];
  hydrateBoards: (ctx: Ctx) => Promise<void>;
  hydrateBoard: (boardId: string, ctx: Ctx) => Promise<void>;
  createBoard: (
    input: { title?: string; author: string; prompt?: string } & Ctx
  ) => string;
  renameBoard: (boardId: string, title: string, ctx: Ctx) => void;
  deleteBoard: (boardId: string, ctx: Ctx) => void;
  duplicateBoard: (boardId: string, ctx: Ctx) => Promise<string | null>;
  /** Whole-board c1 unit of state. Replaces the board's messages +
   *  artifactContent in-memory (the BoardArtifact component owns the backend
   *  PUT on stream-end). */
  setBoardArtifact: (
    boardId: string,
    patch: {
      messages?: BoardMessage[];
      artifactContent?: string;
      title?: string;
      generationStatus?: 'streaming' | 'completed' | 'interrupted' | null;
    }
  ) => void;
}

function touch(board: Board): Board {
  return { ...board, updatedAt: Date.now() };
}

// ── Backend write ordering ────────────────────────────────────────────────
// Board creation is optimistic + fire-and-forget, so a board-dependent fetch
// (e.g. the detail hydrate or artifact PUT) can outrace its board's POST and
// hit the backend before the board row exists (→ 404 "board not found"). We
// serialise board-dependent writes behind the board's create promise. The
// promise resolves even on failure so a genuine create error surfaces
// downstream rather than deadlocking the chain.
const boardReady = new Map<string, Promise<unknown>>();

/** Resolves once the board's create POST has settled. For boards loaded from
 *  the backend (no local create this session) there's no entry → resolves
 *  immediately, since the row already exists. */
function afterBoard(boardId: string): Promise<unknown> {
  return boardReady.get(boardId) ?? Promise.resolve();
}

export const useBoardsStore = create<BoardsState>()((set) => ({
  boards: [],

  hydrateBoards: async (ctx) => {
    const res = await api.listBoards(ctx);
    const incoming = res.boards.map(summaryToBoard);
    set((s) => {
      // Preserve locally-known detail so the summary list (which carries none)
      // never wipes it. Merge by PRESENCE of a prior board, NOT by
      // `artifactContent != null` — a board mid-generation has no artifact yet
      // but DOES have `generationStatus: 'streaming'`, and the old gate dropped
      // that, flipping its in-progress UI back to the empty "get-started" state.
      const existingById = new Map(s.boards.map((b) => [b.id, b]));
      const boards = incoming.map((b) => {
        const prev = existingById.get(b.id);
        return prev
          ? {
              ...b,
              messages: prev.messages,
              artifactContent: prev.artifactContent,
              generationStatus: prev.generationStatus,
            }
          : b;
      });
      return { boards };
    });
  },

  hydrateBoard: async (boardId, ctx) => {
    // A freshly created board's detail fetch can outrace its create POST →
    // getBoard 404s before the row exists. Wait for the create to settle first
    // (no-op for boards loaded from the list — they have no boardReady entry).
    await afterBoard(boardId);
    let d;
    try {
      d = await api.getBoard({ boardUUID: boardId, ...ctx });
    } catch (err) {
      // 404 here = board genuinely not on the backend (stale/deep-linked id, or
      // a create that failed). Don't throw — an unhandled rejection breaks the
      // page. Leave any optimistic local board in place; BoardPage shows a
      // not-found state when the board is truly unknown. A 404 is EXPECTED for a
      // bad id, so warn (not error) to avoid tripping Next's error overlay.
      const is404 = err instanceof Error && /\(404\)/.test(err.message);
      if (is404) console.warn('[boards] board not found', boardId);
      else console.error('[boards] hydrateBoard failed', err);
      return;
    }
    const hydrated = detailToBoard(d);
    set((s) => {
      const prev = s.boards.find((b) => b.id === hydrated.id);
      // A detail-fetch issued before a just-streamed artifact was persisted can
      // return an empty row. Don't let it overwrite newer local content —
      // keep the local artifact/messages when the backend has none yet.
      const merged =
        prev && !hydrated.artifactContent && prev.artifactContent
          ? {
              ...hydrated,
              artifactContent: prev.artifactContent,
              messages: prev.messages,
            }
          : hydrated;
      return {
        boards: prev
          ? s.boards.map((b) => (b.id === merged.id ? merged : b))
          : [merged, ...s.boards],
      };
    });
  },

  createBoard: ({ title, author, orgUUID, userUUID, prompt }) => {
    const id = uuid();
    const now = Date.now();
    set((s) => ({
      boards: [
        {
          id,
          title: title ?? 'Untitled board',
          author,
          createdAt: now,
          updatedAt: now,
        },
        ...s.boards,
      ],
    }));
    // Register the create promise so dependent writes (artifact PUT) wait for
    // the board row to exist. `.catch` keeps it resolving so a real create
    // failure surfaces at the dependent call, not as a hung chain.
    const created = api
      .createBoard({ orgUUID, userUUID, title, author, uuid: id, prompt })
      .then((res) => {
        // When a prompt was sent, the backend names the board with a concise
        // Title-Case title (same titler as /ask-ellie). Reflect it live so the
        // sidebar + header swap from the optimistic prompt to the real title.
        if (res?.title) {
          set((s) => ({
            boards: s.boards.map((b) =>
              b.id === id ? { ...b, title: res.title } : b
            ),
          }));
        }
        return res;
      })
      .catch((err) => console.error('[boards] createBoard failed', err));
    boardReady.set(id, created);
    return id;
  },

  renameBoard: (boardId, title, ctx) => {
    set((s) => ({
      boards: s.boards.map((b) =>
        b.id === boardId ? touch({ ...b, title }) : b
      ),
    }));
    void api
      .updateBoard({ boardUUID: boardId, ...ctx, title })
      .catch((err) => console.error('[boards] renameBoard failed', err));
  },

  deleteBoard: (boardId, ctx) => {
    // Optimistic remove — but capture the board + its position so we can
    // restore it if the DELETE fails (else it silently reappears on refresh
    // with no feedback).
    let removed: Board | undefined;
    let idx = -1;
    set((s) => {
      idx = s.boards.findIndex((b) => b.id === boardId);
      removed = idx >= 0 ? s.boards[idx] : undefined;
      return { boards: s.boards.filter((b) => b.id !== boardId) };
    });
    if (!removed) return;
    void api.deleteBoard({ boardUUID: boardId, ...ctx }).catch((err) => {
      console.error('[boards] deleteBoard failed', err);
      set((s) => {
        if (s.boards.some((b) => b.id === boardId)) return s; // already restored
        const next = [...s.boards];
        next.splice(Math.min(idx, next.length), 0, removed as Board);
        return { boards: next };
      });
      toast.error("Couldn't delete the board. Please try again.");
    });
  },

  duplicateBoard: async (boardId, ctx) => {
    try {
      const d = await api.duplicateBoard({ boardUUID: boardId, ...ctx });
      const copy = detailToBoard(d);
      set((s) => ({ boards: [copy, ...s.boards] }));
      return copy.id;
    } catch (err) {
      console.error('[boards] duplicateBoard failed', err);
      return null;
    }
  },

  setBoardArtifact: (boardId, patch) =>
    // In-memory only — BoardArtifact owns the backend PUT on stream-end so we
    // don't write on every streaming chunk.
    set((s) => ({
      boards: s.boards.map((b) =>
        b.id === boardId ? touch({ ...b, ...patch }) : b
      ),
    })),
}));
